/**
 * Generic In-Memory Cache Utility
 * 
 * Provides TTL-based caching with automatic eviction
 */

interface CacheEntry<T> {
  value: T
  expiresAt: number
}

/**
 * Generic TTL Cache with automatic eviction
 * 
 * @example
 * const cache = new TTLCache<UserData>({ ttl: 5 * 60 * 1000, maxEntries: 100 })
 * cache.set('user:123', userData)
 * const user = cache.get('user:123') // null if expired
 */
export class TTLCache<T> {
  private readonly cache = new Map<string, CacheEntry<T>>()
  private readonly ttl: number
  private readonly maxEntries: number

  constructor(options: { ttl: number; maxEntries?: number }) {
    this.ttl = options.ttl
    this.maxEntries = options.maxEntries ?? 256
  }

  get(key: string): T | null {
    const entry = this.cache.get(key)
    if (!entry) return null

    if (entry.expiresAt <= Date.now()) {
      this.cache.delete(key)
      return null
    }

    return entry.value
  }

  set(key: string, value: T, customTtl?: number): void {
    const ttl = customTtl ?? this.ttl
    if (ttl <= 0) return

    this.cache.set(key, {
      value,
      expiresAt: Date.now() + ttl
    })

    // Evict oldest entry if over capacity
    if (this.cache.size > this.maxEntries) {
      const oldestKey = this.cache.keys().next().value
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey)
      }
    }
  }

  delete(key: string): boolean {
    return this.cache.delete(key)
  }

  has(key: string): boolean {
    return this.get(key) !== null
  }

  clear(): void {
    this.cache.clear()
  }

  /**
   * Get or compute value (cache-aside pattern)
   */
  async getOrSet(key: string, factory: () => Promise<T>, customTtl?: number): Promise<T> {
    const cached = this.get(key)
    if (cached !== null) return cached

    const value = await factory()
    this.set(key, value, customTtl)
    return value
  }

  /**
   * Get or compute value synchronously
   */
  getOrSetSync(key: string, factory: () => T, customTtl?: number): T {
    const cached = this.get(key)
    if (cached !== null) return cached

    const value = factory()
    this.set(key, value, customTtl)
    return value
  }

  /**
   * Delete all keys matching prefix
   */
  deleteByPrefix(prefix: string): number {
    let count = 0
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key)
        count++
      }
    }
    return count
  }

  /**
   * Get all keys in cache (filtering expired)
   */
  getAllKeys(): string[] {
    const keys: string[] = []
    for (const key of this.cache.keys()) {
      if (this.get(key) !== null) {
        keys.push(key)
      }
    }
    return keys
  }

  /**
   * Get cache stats
   */
  stats(): { size: number; maxEntries: number; ttl: number } {
    return {
      size: this.cache.size,
      maxEntries: this.maxEntries,
      ttl: this.ttl
    }
  }
}

/**
 * Decorator for caching method results
 * 
 * @example
 * const cache = new TTLCache<User>({ ttl: 60000 })
 * const getUser = withCache(cache, (id: string) => fetchUser(id), (id) => `user:${id}`)
 */
export function withCache<T, Args extends unknown[]>(
  cache: TTLCache<T>,
  fn: (...args: Args) => Promise<T>,
  keyBuilder: (...args: Args) => string
): (...args: Args) => Promise<T> {
  return async (...args: Args): Promise<T> => {
    const key = keyBuilder(...args)
    return cache.getOrSet(key, () => fn(...args))
  }
}
